// Network layer for the unread messaging indicator.
//
// The Async Messaging SDK ships the inbox / thread-detail / compose screens and
// marks threads read on open, but exposes no unread-count hook, so the app
// reads the count itself from the thread-list route, which carries it at
// `meta.unreadCount` in both versions of the route.
//
// `leagueFetch` attaches the member's session and auth, and the active plan is
// resolved server-side from that session, so the request takes no plan id — a
// plan switch changes the session and a refetch returns the new plan's count.

import { leagueFetch } from '@leagueplatform/league-fetch';

// The SDK picks its thread-list route from a feature flag, so this has to follow
// it. Reading a different version than the inbox does would let the indicator
// disagree with the threads the member can actually see.
const THREADS_PATH = '/v1/messaging-threads';
const THREADS_PATH_V2 = '/v2/messaging-threads';

// Only the count in `meta` is wanted, never the threads themselves, so the page
// is squeezed to the smallest the route will serve — it defaults to 10, and
// thread subjects and participants are member health content that should not
// cross the wire to feed a dot. `meta.unreadCount` is aggregated over the whole
// inbox before the page is sliced, so shrinking the page does not shrink it.
const SMALLEST_PAGE = 'page[size]=1';

const getUnreadMessageCountPath = (isThreadsV2Enabled: boolean) =>
  `${isThreadsV2Enabled ? THREADS_PATH_V2 : THREADS_PATH}?${SMALLEST_PAGE}`;

type UnreadMessageThreadsResponse = {
  meta?: {
    unreadCount?: number;
  };
};

/**
 * Reads the unread messaging-thread count for the member's active plan. An
 * absent count normalizes to 0 so the indicator simply hides rather than
 * erroring — the count is aggregated over the full inbox, not the first page.
 */
export const getUnreadMessageCount = async (
  isThreadsV2Enabled: boolean,
): Promise<number> => {
  const response = await leagueFetch(
    getUnreadMessageCountPath(isThreadsV2Enabled),
  );

  if (!response.ok) {
    throw new Error(`Failed to load unread message count: ${response.status}`);
  }

  const body: UnreadMessageThreadsResponse = await response.json();
  return body.meta?.unreadCount ?? 0;
};
